import { REALTIME_SUBSCRIBE_STATES } from "@supabase/realtime-js";
import { createClient, type RealtimeChannel } from "@supabase/supabase-js";

import type { Lane, RemoteDriver, TrafficEvent } from "../../types/traffic";
import {
  canPublishTrafficEvent,
  collectPresenceDrivers,
  isEventFromKnownRemote,
  parsePresenceState,
  parseTrafficEvent,
  type CohortConnectionInput,
  type RealtimeListener,
  type TrafficRealtimeProvider,
} from "./TrafficRealtimeProvider";
import { recordRealtimeDiagnostic } from "./realtimeDiagnostics";

export class SupabaseTrafficRealtimeProvider implements TrafficRealtimeProvider {
  private readonly client: ReturnType<typeof createClient>;
  private readonly listeners = new Set<RealtimeListener>();
  private readonly remoteSessionIds = new Set<string>();
  private readonly lastReceivedEventAt = new Map<string, number>();
  private channel: RealtimeChannel | null = null;
  private connectedChannel: string | null = null;
  private currentDriver: RemoteDriver | null = null;
  private subscribed = false;
  private healTimer: ReturnType<typeof setTimeout> | null = null;
  private laneTrackTimer: ReturnType<typeof setTimeout> | null = null;
  private presenceTrackChain: Promise<void> = Promise.resolve();

  public constructor(url: string, anonKey: string) {
    this.client = createClient(url, anonKey, {
      auth: {
        autoRefreshToken: false,
        detectSessionInUrl: false,
        persistSession: false,
      },
      realtime: {
        params: { eventsPerSecond: 8 },
      },
    });
  }

  public async connect(input: CohortConnectionInput): Promise<void> {
    recordRealtimeDiagnostic("radio.connect.requested", {
      channel: input.channel,
      sessionId: input.sessionId,
    });
    await this.openChannel(input, true);
  }

  public async reconnect(input: CohortConnectionInput): Promise<void> {
    recordRealtimeDiagnostic("radio.reconnect.forced", {
      channel: input.channel,
      sessionId: input.sessionId,
    });
    await this.openChannel(input, false, true);
  }

  public updateLane(lane: Lane): Promise<void> {
    if (!this.currentDriver) return Promise.resolve();
    this.currentDriver = { ...this.currentDriver, lane };
    if (this.laneTrackTimer !== null) clearTimeout(this.laneTrackTimer);
    this.laneTrackTimer = setTimeout(() => {
      this.laneTrackTimer = null;
      const driver = this.currentDriver;
      if (!driver) return;
      void this.updatePresence(driver);
    }, 320);
    return Promise.resolve();
  }

  public updatePresence(driver: RemoteDriver): Promise<void> {
    this.currentDriver = driver;
    if (this.subscribed && this.channel) {
      const channel = this.channel;
      this.presenceTrackChain = this.presenceTrackChain
        .catch(() => undefined)
        .then(async () => {
          const latestDriver = this.currentDriver;
          if (!latestDriver || !this.subscribed || this.channel !== channel) {
            return;
          }
          await channel.track(latestDriver);
          recordRealtimeDiagnostic("presence.track.succeeded", {
            channel: this.connectedChannel,
            sessionId: latestDriver.sessionId,
          });
        })
        .catch((error: unknown) => {
          if (this.channel !== channel) return;
          this.subscribed = false;
          recordRealtimeDiagnostic("presence.track.failed", {
            channel: this.connectedChannel,
            message: error instanceof Error ? error.message : "unknown",
          });
          this.emit({ type: "connection", state: "connecting" });
          this.scheduleSilentHeal();
        });
      return this.presenceTrackChain;
    }
    this.scheduleSilentHeal();
    return Promise.resolve();
  }

  public async publish(event: TrafficEvent): Promise<void> {
    if (
      !this.channel ||
      !canPublishTrafficEvent(
        event,
        this.currentDriver?.sessionId,
        this.subscribed,
      )
    ) {
      return;
    }
    await this.channel.send({
      type: "broadcast",
      event: "traffic",
      payload: event,
    });
  }

  public async disconnect(): Promise<void> {
    await this.tearDown(false);
  }

  public isConnected(): boolean {
    return this.subscribed && this.channel !== null;
  }

  public subscribe(listener: RealtimeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async openChannel(
    input: CohortConnectionInput,
    announceConnecting: boolean,
    force = false,
  ): Promise<void> {
    if (
      !force &&
      this.subscribed &&
      this.channel &&
      this.connectedChannel === input.channel &&
      this.currentDriver?.sessionId === input.sessionId
    ) {
      await this.updatePresence(input.driver);
      this.emit({ type: "connection", state: "connected" });
      return;
    }

    await this.tearDown(true);
    if (announceConnecting) {
      this.emit({ type: "connection", state: "connecting" });
    }
    this.currentDriver = input.driver;
    this.connectedChannel = input.channel;
    const channel = this.client.channel(input.channel, {
      config: {
        presence: { key: input.sessionId },
        broadcast: { self: false },
      },
    });
    this.channel = channel;

    channel
      .on("presence", { event: "sync" }, () => this.emitPresenceSnapshot())
      .on("presence", { event: "join" }, ({ newPresences }) => {
        for (const value of newPresences) {
          const driver = parsePresenceState(value);
          if (
            driver &&
            driver.sessionId !== input.sessionId &&
            driver.publicDriverId !== input.driver.publicDriverId
          ) {
            this.remoteSessionIds.add(driver.sessionId);
            this.emit({ type: "join", driver });
          }
        }
      })
      .on("presence", { event: "leave" }, ({ key, leftPresences }) => {
        const sessionIds = new Set<string>();
        if (typeof key === "string" && key !== input.sessionId) {
          sessionIds.add(key);
        }
        for (const value of leftPresences) {
          const driver = parsePresenceState(value);
          if (driver && driver.sessionId !== input.sessionId) {
            sessionIds.add(driver.sessionId);
          }
        }
        for (const sessionId of sessionIds) {
          // Keep accepting this driver's broadcasts during the store's leave
          // grace. A subsequent presence snapshot atomically replaces the set.
          this.emit({ type: "leave", sessionId });
        }
      })
      .on("broadcast", { event: "traffic" }, ({ payload }) => {
        const event = parseTrafficEvent(payload);
        if (!event) return;
        if (!isEventFromKnownRemote(event, this.remoteSessionIds)) return;
        if (event.type === "exit") {
          this.remoteSessionIds.delete(event.sessionId);
          this.emit({ type: "event", event });
          return;
        }
        if (event.type === "lane") {
          this.emit({ type: "event", event });
          return;
        }
        const throttleKey = `${event.sessionId}:${event.type}`;
        const lastAt = this.lastReceivedEventAt.get(throttleKey) ?? 0;
        if (Date.now() - lastAt < 400) return;
        this.lastReceivedEventAt.set(throttleKey, Date.now());
        this.emit({ type: "event", event });
      });

    await new Promise<void>((resolve, reject) => {
      let opened = false;
      void channel.subscribe((status) => {
        if (this.channel !== channel) return;
        recordRealtimeDiagnostic("radio.channel.status", {
          channel: input.channel,
          status,
        });
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          this.subscribed = true;
          this.clearHealTimer();
          void channel
            .track(input.driver)
            .then(() => {
              opened = true;
              this.emitPresenceSnapshot();
              recordRealtimeDiagnostic("radio.connected", {
                channel: input.channel,
                sessionId: input.sessionId,
              });
              this.emit({ type: "connection", state: "connected" });
              resolve();
            })
            .catch((error: unknown) => {
              this.subscribed = false;
              reject(
                error instanceof Error
                  ? error
                  : new Error("Presence track failed."),
              );
            });
          return;
        }
        if (!opened) {
          this.subscribed = false;
          this.emit({ type: "connection", state: "offline" });
          reject(new Error(`Realtime channel failed: ${status}`));
          return;
        }
        this.subscribed = false;
        this.emit({ type: "connection", state: "connecting" });
        this.scheduleSilentHeal();
      });
    });
  }

  private scheduleSilentHeal(): void {
    if (this.healTimer !== null || this.subscribed) return;
    const driver = this.currentDriver;
    const channelName = this.connectedChannel;
    if (!driver || !channelName) return;
    recordRealtimeDiagnostic("radio.heal.scheduled", {
      channel: channelName,
      sessionId: driver.sessionId,
      delayMs: 1_600,
    });
    this.healTimer = setTimeout(() => {
      this.healTimer = null;
      if (this.subscribed || !this.currentDriver || !this.connectedChannel) {
        return;
      }
      void this.openChannel(
        {
          sessionId: this.currentDriver.sessionId,
          channel: this.connectedChannel,
          driver: this.currentDriver,
        },
        false,
      ).catch(() => {
        recordRealtimeDiagnostic("radio.heal.failed", {
          channel: this.connectedChannel,
        });
        this.emit({ type: "connection", state: "offline" });
      });
    }, 1_600);
  }

  private clearHealTimer(): void {
    if (this.healTimer !== null) {
      clearTimeout(this.healTimer);
      this.healTimer = null;
    }
  }

  private async tearDown(silent: boolean): Promise<void> {
    this.clearHealTimer();
    if (this.laneTrackTimer !== null) {
      clearTimeout(this.laneTrackTimer);
      this.laneTrackTimer = null;
    }
    this.subscribed = false;
    const channel = this.channel;
    this.channel = null;
    this.remoteSessionIds.clear();
    if (!silent) {
      this.connectedChannel = null;
      this.currentDriver = null;
    }
    if (channel) {
      try {
        await channel.untrack();
      } catch {
        // Channel may already be closed.
      }
      try {
        await this.client.removeChannel(channel);
      } catch {
        // Removing a closed channel should not surface as a radio outage.
      }
    }
    if (!silent) this.emit({ type: "connection", state: "offline" });
  }

  private emitPresenceSnapshot(): void {
    if (!this.channel) return;
    try {
      const drivers = collectPresenceDrivers(
        this.channel.presenceState(),
        this.currentDriver?.sessionId,
        this.currentDriver?.publicDriverId,
      );
      this.remoteSessionIds.clear();
      for (const driver of drivers) {
        this.remoteSessionIds.add(driver.sessionId);
      }
      recordRealtimeDiagnostic("presence.snapshot", {
        channel: this.connectedChannel,
        count: drivers.length,
        sessionIds: drivers.map((driver) => driver.sessionId),
      });
      this.emit({ type: "snapshot", drivers });
    } catch (error: unknown) {
      recordRealtimeDiagnostic("presence.snapshot.failed", {
        channel: this.connectedChannel,
        message: error instanceof Error ? error.message : "unknown",
      });
      // A malformed presence payload must not drop the radio.
    }
  }

  private emit(update: Parameters<RealtimeListener>[0]): void {
    this.listeners.forEach((listener) => listener(update));
  }
}
